import type { ToolConfig } from "../types";

export const legalTools: ToolConfig[] = [
  // 1. Court Fee Calculator
  {
    slug: "court-fee-calculator",
    category: "legal",
    title: "Court Fee Calculator for Civil Suits",
    shortTitle: "Court Fee",
    description: "Calculate court fees for civil cases in India",
    keywords: ["court", "fee", "suit", "value", "civil", "litigation"],
    inputs: [
      { type: "number", key: "suitValue", label: "Suit Value / Claim Amount", prefix: "₹", min: 0, max: 100000000, step: 10000, default: 500000 },
    ],
    calculate(v) {
      const value = v.suitValue as number;

      // Court fee under CPC (Indian civil courts) - approximate rates
      let fee = 0;
      if (value <= 100000) fee = value * 0.03; // 3% up to ₹100K
      else if (value <= 500000) fee = 3000 + (value - 100000) * 0.05; // 3% of first 100K + 5%
      else if (value <= 5000000) fee = 23000 + (value - 500000) * 0.1; // +10% above 500K
      else fee = 473000 + (value - 5000000) * 0.15; // +15% above 5M

      fee = Math.max(100, fee); // Minimum fee

      return {
        suitValue: Math.round(value),
        courtFee: Math.round(fee),
      };
    },
    resultFields: [
      { key: "courtFee", label: "Court Fee to be Deposited", format: "currency", isPrimary: true },
      { key: "suitValue", label: "Suit Value / Claim Amount", format: "currency" },
    ],
    content: {
      whatIs: "Court fee is a tax paid to file a civil suit. Amount depends on suit value and varies: 3% on first ₹100K, then 5%, 10%, 15% on higher slabs. Court won't accept suit without fee deposit.",
      faqs: [
        {
          question: "What determines the suit value?",
          answer: "Amount of money you're claiming (compensation, damages, recovery). If suing for property, use market value. If claiming damages, use estimated loss. Be accurate—inflated values pay higher fees; low values may be dismissed.",
        },
        {
          question: "Can I negotiate or waive court fee?",
          answer: "No. Court fee is fixed by law, non-negotiable. However, you can request waiver if poor/unable to pay (In Forma Pauperis). Courts rarely grant waivers; need strong justification.",
        },
        {
          question: "Is court fee refundable if I lose?",
          answer: "No. Court fee is non-refundable regardless of judgment. If opponent is held liable for costs, you might recover part of legal fees (not court fee). Court fee is gone once paid.",
        },
        {
          question: "What if suit value is disputed?",
          answer: "If defendant contests suit value, court decides. If value is inflated, court might reduce it. You'd pay excess court fee (refund if value reduced). Always claim realistic value.",
        },
      ],
      relatedSlugs: ["legal/advocate-fee-estimator", "legal/legal-notice-cost"],
    },
  },

  // 2. Advocate Fee Estimator
  {
    slug: "advocate-fee-estimator",
    category: "legal",
    title: "Advocate/Lawyer Fee Estimator",
    shortTitle: "Lawyer Fees",
    description: "Estimate advocate fee for legal services in India",
    keywords: ["lawyer", "advocate", "fee", "cost", "legal", "case"],
    inputs: [
      { type: "select", key: "caseType", label: "Case Type", options: [
        { value: "criminal", label: "Criminal (Bail, Defense)" },
        { value: "civil", label: "Civil (Property, Contract, Money)" },
        { value: "family", label: "Family (Divorce, Custody, Succession)" },
        { value: "commercial", label: "Commercial/Corporate" },
        { value: "labor", label: "Labor & Employment" },
        { value: "other", label: "Other" }
      ], default: "civil" },
      { type: "select", key: "complexity", label: "Case Complexity", options: [
        { value: "simple", label: "Simple (Straightforward)" },
        { value: "moderate", label: "Moderate (Some Complexity)" },
        { value: "complex", label: "Complex (Involved, Multiple Issues)" }
      ], default: "moderate" },
      { type: "number", key: "caseValue", label: "Claim/Case Value (If Applicable)", prefix: "₹", min: 0, max: 50000000, step: 100000, default: 500000 },
    ],
    calculate(v) {
      const type = v.caseType as string;
      const complexity = v.complexity as string;
      const value = v.caseValue as number;

      // Base fees by case type and complexity (rough estimates)
      const baseFees: Record<string, Record<string, number>> = {
        criminal: { simple: 15000, moderate: 40000, complex: 100000 },
        civil: { simple: 20000, moderate: 50000, complex: 150000 },
        family: { simple: 15000, moderate: 40000, complex: 80000 },
        commercial: { simple: 30000, moderate: 80000, complex: 250000 },
        labor: { simple: 10000, moderate: 30000, complex: 70000 },
        other: { simple: 15000, moderate: 35000, complex: 80000 },
      };

      const baseFee = baseFees[type]?.[complexity] ?? 30000;

      // Additional percentage of case value (if applicable)
      const percentageComponent = type !== "family" && type !== "criminal" ? (value * 0.02) : 0; // 2% of case value

      const totalFee = baseFee + percentageComponent;

      // Estimates
      const lowEstimate = totalFee * 0.7;
      const highEstimate = totalFee * 1.5;

      return {
        caseType: type,
        complexity: complexity,
        baseFee: Math.round(baseFee),
        percentageComponent: Math.round(percentageComponent),
        estimatedFee: Math.round(totalFee),
        lowEstimate: Math.round(lowEstimate),
        highEstimate: Math.round(highEstimate),
      };
    },
    resultFields: [
      { key: "estimatedFee", label: "Estimated Lawyer Fee", format: "currency", isPrimary: true },
      { key: "lowEstimate", label: "Low Estimate (Budget)", format: "currency" },
      { key: "highEstimate", label: "High Estimate (Premium)", format: "currency" },
      { key: "baseFee", label: "Base Fee", format: "currency" },
    ],
    content: {
      whatIs: "Lawyer fees in India vary widely by case type, complexity, experience of lawyer, and location. This gives rough estimates. Always consult and negotiate with actual lawyers for accurate quotes.",
      faqs: [
        {
          question: "How are lawyer fees typically charged?",
          answer: "Methods: 1) Fixed fee (flat amount), 2) Hourly (e.g., ₹2K-10K/hour), 3) Contingency (% of award if you win), 4) Retainer (monthly for ongoing). Civil/commercial usually fixed or hourly. Family/criminal often fixed.",
        },
        {
          question: "What's included in lawyer fees?",
          answer: "Varies. Typically: initial consultation, drafting, advice. Extra charges for: court appearances, appeals, detailed research, opposition arguments. Clarify scope in retainer agreement.",
        },
        {
          question: "Can I find affordable lawyers?",
          answer: "Yes. Legal aid clinics (free/low-cost for poor), junior lawyers (cheaper), online legal services. Avoid paying huge upfront; negotiate installment payments. Ask for itemized billing.",
        },
        {
          question: "Should I negotiate lawyer fees?",
          answer: "Absolutely. Fees are negotiable. Compare quotes from 2-3 lawyers. Junior advocates are cheaper; senior ones cost more. Negotiate scope, payment terms, and deliverables.",
        },
        {
          question: "What if I lose the case?",
          answer: "You still pay lawyer fees (unless contingency agreed). Court might order loser to pay some opponent's legal costs. Budget for worst-case scenario.",
        },
      ],
      relatedSlugs: ["legal/court-fee-calculator", "legal/legal-notice-cost"],
    },
  },

  // 3. Arbitration Cost Calculator
  {
    slug: "arbitration-cost-calculator",
    category: "legal",
    title: "Arbitration Cost Calculator",
    shortTitle: "Arbitration Cost",
    description: "Calculate arbitration costs and fees in India",
    keywords: ["arbitration", "cost", "dispute", "resolution", "adr", "fee"],
    inputs: [
      { type: "number", key: "claimAmount", label: "Claim Amount", prefix: "₹", min: 0, max: 100000000, step: 100000, default: 1000000 },
      { type: "select", key: "arbitrators", label: "Number of Arbitrators", options: [
        { value: "1", label: "Single Arbitrator" },
        { value: "3", label: "Three Arbitrators (Panel)" }
      ], default: "1" },
    ],
    calculate(v) {
      const amount = v.claimAmount as number;
      const arbCount = parseInt(v.arbitrators as string);

      // Arbitration fee structure (SIAC/LCIA type, approximate)
      let fee = 0;
      if (amount <= 500000) fee = 25000;
      else if (amount <= 5000000) fee = 50000 + (amount - 500000) * 0.005;
      else if (amount <= 50000000) fee = 72500 + (amount - 5000000) * 0.002;
      else fee = 162500 + (amount - 50000000) * 0.001;

      // Panel arbitration costs more (3 arbitrators)
      fee = fee * (arbCount === 3 ? 2.5 : 1);

      // Add hearing costs, administrative fees
      const hearingCosts = (fee * 0.3);
      const totalCost = fee + hearingCosts;

      return {
        claimAmount: Math.round(amount),
        arbitratorCount: arbCount,
        arbitrationFee: Math.round(fee),
        hearingAndAdmin: Math.round(hearingCosts),
        totalArbitrationCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalArbitrationCost", label: "Total Arbitration Cost", format: "currency", isPrimary: true },
      { key: "arbitrationFee", label: "Arbitrator Fee", format: "currency" },
      { key: "hearingAndAdmin", label: "Hearing & Administrative Costs", format: "currency" },
      { key: "claimAmount", label: "Claim Amount", format: "currency" },
    ],
    content: {
      whatIs: "Arbitration is an alternative to court litigation. Faster (6-18 months vs 3-5 years), private, enforceable. Costs depend on claim amount and arbitrator selection. Usually cheaper than lengthy court cases.",
      faqs: [
        {
          question: "When is arbitration preferred over court?",
          answer: "When: 1) Speed needed, 2) Confidentiality required, 3) Technical expertise needed (construction, IT), 4) Both parties agreed to arbitration clause, 5) Parties want finality (appeal limited).",
        },
        {
          question: "Can I arbitrate any dispute?",
          answer: "No. Criminal matters, marriage, succession not arbitrable. Most commercial, contractual, property disputes can be arbitrated if both parties agreed before dispute. Consent is key.",
        },
        {
          question: "Is arbitration award enforceable?",
          answer: "Yes, under Indian law. Award is binding and final. Limited grounds for challenge. If party doesn't comply, you can enforce through court (quicker than litigation).",
        },
        {
          question: "Who bears arbitration costs?",
          answer: "Depends on agreement. Usually split 50-50. If one party is clearly wrong, arbitrator can order them to pay all costs. Clarify cost-sharing in arbitration agreement.",
        },
        {
          question: "Can I recover lawyer fees in arbitration?",
          answer: "Arbitrator might award reasonable legal fees as part of cost order if one party is wrongfully defending. Not automatic; depends on case merits and arbitrator's discretion.",
        },
      ],
      relatedSlugs: ["legal/court-fee-calculator", "legal/legal-notice-cost"],
    },
  },

  // 4. Legal Notice Cost
  {
    slug: "legal-notice-cost",
    category: "legal",
    title: "Legal Notice Cost Calculator",
    shortTitle: "Legal Notice Cost",
    description: "Calculate legal notice drafting and service cost",
    keywords: ["notice", "legal", "cost", "demand", "lawyer", "warning"],
    inputs: [
      { type: "select", key: "noticeType", label: "Type of Notice", options: [
        { value: "demand", label: "Demand Notice (Demand for Payment)" },
        { value: "warning", label: "Warning Notice (Cease & Desist)" },
        { value: "termination", label: "Termination Notice (Contract/Rent)" },
        { value: "other", label: "Other Notice" }
      ], default: "demand" },
      { type: "toggle", key: "withSignature", label: "Registered with Signature?", default: true, helpText: "R.P.A.D or Registered Post" },
    ],
    calculate(v) {
      const type = v.noticeType as string;
      const registered = v.withSignature as boolean;

      // Lawyer fee for notice (varies)
      const lawyerFees: Record<string, number> = {
        demand: 3000,
        warning: 2500,
        termination: 2500,
        other: 3000,
      };

      const fee = lawyerFees[type] ?? 3000;

      // Postal costs
      const postalCost = registered ? 200 : 50; // R.P.A.D or regular post

      // Stamp (for some notices)
      const stampDuty = 50;

      const totalCost = fee + postalCost + stampDuty;

      return {
        noticeType: type,
        lawyerFee: Math.round(fee),
        postalCost: Math.round(postalCost),
        stampDuty: Math.round(stampDuty),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total Legal Notice Cost", format: "currency", isPrimary: true },
      { key: "lawyerFee", label: "Lawyer Fee for Drafting", format: "currency" },
      { key: "postalCost", label: "Postal/Courier Charges", format: "currency" },
      { key: "stampDuty", label: "Stamp Duty", format: "currency" },
    ],
    content: {
      whatIs: "A legal notice is a formal written demand or warning from a lawyer. Precedes court action. Shows seriousness, creates legal record. Must be sent registered (with proof of receipt) for validity. Cost: ₹2.5-5K typically.",
      faqs: [
        {
          question: "Do I need a lawyer to send a legal notice?",
          answer: "Not legally required, but highly recommended. Lawyer-signed notice carries weight; recipient takes it seriously. Shows you're considering legal action. Self-drafted notices are weak evidence.",
        },
        {
          question: "How should legal notice be sent?",
          answer: "Always use R.P.A.D. (Registered Post with Acknowledgment Due) or courier with tracking. Proof of delivery is crucial for court. Never use email alone or regular post.",
        },
        {
          question: "What's response time to legal notice?",
          answer: "Usually 15-30 days to respond/comply. Varies by notice. If no response, proceed to court/arbitration. Notice creates legal trail; ignore it at recipient's risk.",
        },
        {
          question: "Can I send notice myself?",
          answer: "Yes, legally possible, but weak. Use lawyer for: proper legal language, formal approach, professional impact, proof of service. ₹3K investment often prevents expensive litigation.",
        },
      ],
      relatedSlugs: ["legal/court-fee-calculator", "legal/advocate-fee-estimator"],
    },
  },

  // 5. Affidavit Stamp Duty
  {
    slug: "affidavit-stamp-duty",
    category: "legal",
    title: "Affidavit Stamp Duty Calculator by State",
    shortTitle: "Affidavit Stamp Duty",
    description: "Calculate stamp duty on affidavits in India",
    keywords: ["affidavit", "stamp", "duty", "state", "cost"],
    inputs: [
      { type: "select", key: "state", label: "State", options: [
        { value: "maharashtra", label: "Maharashtra" },
        { value: "karnataka", label: "Karnataka" },
        { value: "tamil_nadu", label: "Tamil Nadu" },
        { value: "delhi", label: "Delhi" },
        { value: "punjab", label: "Punjab" },
        { value: "rajasthan", label: "Rajasthan" },
        { value: "others", label: "Others (Approx)" }
      ], default: "maharashtra" },
      { type: "number", key: "affidavitCount", label: "Number of Affidavits", suffix: "copies", min: 1, max: 10, step: 1, default: 1 },
    ],
    calculate(v) {
      const state = v.state as string;
      const count = v.affidavitCount as number;

      // Stamp duty on affidavits by state (approximate, per affidavit)
      const stampDuty: Record<string, number> = {
        maharashtra: 10, // ₹10 per affidavit
        karnataka: 5,
        tamil_nadu: 10,
        delhi: 10,
        punjab: 5,
        rajasthan: 5,
        others: 10,
      };

      const duty = stampDuty[state] ?? 10;
      const totalStampDuty = duty * count;

      // Notary fee
      const notaryFee = 500 * count;

      const totalCost = totalStampDuty + notaryFee;

      return {
        state: state,
        affidavitCount: count,
        stampDutyPerAffidavit: duty,
        totalStampDuty: Math.round(totalStampDuty),
        notaryFee: Math.round(notaryFee),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total Affidavit Cost", format: "currency", isPrimary: true },
      { key: "totalStampDuty", label: "Stamp Duty", format: "currency" },
      { key: "notaryFee", label: "Notary Charges", format: "currency" },
    ],
    content: {
      whatIs: "Affidavit is a sworn statement before notary. Used in courts, banks, visa applications. Stamp duty varies by state (₹5-10 typically). Cost is minimal; validity is critical (must be notarized).",
      faqs: [
        {
          question: "What's the difference between affidavit and declaration?",
          answer: "Affidavit: Sworn before notary, legally binding, false affidavit is perjury. Declaration: Simple statement, less formal, no notary needed. For important matters (court, visa), use affidavit.",
        },
        {
          question: "Who can notarize an affidavit?",
          answer: "Notary public (licensed), judge, magistrate, lawyer. Never notarize own affidavit. Affiant and notary must be different people. Cost: ~₹500 per affidavit.",
        },
        {
          question: "How long is an affidavit valid?",
          answer: "Usually 6-12 months from notarization. For government (visa, passport), some allow 1-3 year old affidavits. For court, usually within 1-2 years. Check specific requirement.",
        },
        {
          question: "Can I use old affidavits?",
          answer: "Depends on purpose. Banks/visa: usually need recent (< 1 year). Courts: depends on what's being certified. Safer to get fresh affidavit if unclear.",
        },
      ],
      relatedSlugs: ["legal/notarization-fee-calculator", "legal/will-registration-cost"],
    },
  },

  // 6. Notarization Fee Calculator
  {
    slug: "notarization-fee-calculator",
    category: "legal",
    title: "Notarization Fee Calculator",
    shortTitle: "Notarization Fee",
    description: "Calculate notarization fee for document service",
    keywords: ["notary", "notarization", "fee", "document", "certification"],
    inputs: [
      { type: "select", key: "documentType", label: "Document Type", options: [
        { value: "affidavit", label: "Affidavit" },
        { value: "agreement", label: "Agreement/Contract" },
        { value: "powerOfAttorney", label: "Power of Attorney" },
        { value: "deed", label: "Deed/Certificate" },
        { value: "other", label: "Other Document" }
      ], default: "affidavit" },
      { type: "number", key: "documentCount", label: "Number of Documents", suffix: "copies", min: 1, max: 20, step: 1, default: 1 },
    ],
    calculate(v) {
      const type = v.documentType as string;
      const count = v.documentCount as number;

      // Notary fees (per document)
      const notaryFees: Record<string, number> = {
        affidavit: 500,
        agreement: 750,
        powerOfAttorney: 750,
        deed: 1000,
        other: 500,
      };

      const feePerDoc = notaryFees[type] ?? 500;
      const totalFee = feePerDoc * count;

      return {
        documentType: type,
        documentCount: count,
        feePerDocument: Math.round(feePerDoc),
        totalFee: Math.round(totalFee),
      };
    },
    resultFields: [
      { key: "totalFee", label: "Total Notarization Fee", format: "currency", isPrimary: true },
      { key: "feePerDocument", label: "Fee Per Document", format: "currency" },
      { key: "documentCount", label: "Number of Documents", format: "number" },
    ],
    content: {
      whatIs: "Notarization is official certification that documents are genuine. Notary public charges fee (₹500-1000 per document). Required for: affidavits, agreements, POA, international documents.",
      faqs: [
        {
          question: "Is notarization mandatory for all documents?",
          answer: "No. Only when law requires or parties want certification. Examples: Affidavits (mandatory for court), POA (often required), agreements (optional but recommended), passports (required).",
        },
        {
          question: "Can I notarize my own document?",
          answer: "No. Notary must be independent. Notary verifies your identity, witnesses your signature, certifies genuineness. Never notarize own documents.",
        },
        {
          question: "How long does notarization take?",
          answer: "Usually 10-15 minutes. You must appear in person with ID proof. Notary verifies identity, you sign, notary signs and seals. Instant certification.",
        },
      ],
      relatedSlugs: ["legal/affidavit-stamp-duty", "legal/will-registration-cost"],
    },
  },

  // 7. Will Registration Cost
  {
    slug: "will-registration-cost",
    category: "legal",
    title: "Will Registration Cost Calculator",
    shortTitle: "Will Registration",
    description: "Calculate will registration cost and fees",
    keywords: ["will", "registration", "cost", "stamp", "legal", "succession"],
    inputs: [
      { type: "number", key: "estateValue", label: "Total Estate Value", prefix: "₹", min: 0, max: 100000000, step: 100000, default: 5000000 },
      { type: "toggle", key: "withLawyer", label: "Use Lawyer to Draft Will?", default: true },
    ],
    calculate(v) {
      const estate = v.estateValue as number;
      const withLawyer = v.withLawyer as boolean;

      // Stamp duty on Will (varies by state, ~₹100-200)
      const stampDuty = 100;

      // Registration fee (one-time)
      const registrationFee = 500;

      // Lawyer fees for drafting (if used)
      const lawyerFee = withLawyer ? 5000 : 0; // ₹5K-10K typical

      // Attestation (witnesses): ~2 witnesses required, each charges ~₹500-1000
      const attestationFee = 1000;

      const totalCost = stampDuty + registrationFee + lawyerFee + attestationFee;

      return {
        estateValue: Math.round(estate),
        stampDuty: Math.round(stampDuty),
        registrationFee: Math.round(registrationFee),
        lawyerFee: Math.round(lawyerFee),
        attestationFee: Math.round(attestationFee),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total Will Registration Cost", format: "currency", isPrimary: true },
      { key: "stampDuty", label: "Stamp Duty", format: "currency" },
      { key: "registrationFee", label: "Registration Fee", format: "currency" },
      { key: "lawyerFee", label: "Lawyer Drafting Fee", format: "currency" },
      { key: "attestationFee", label: "Attestation/Witness Fee", format: "currency" },
    ],
    content: {
      whatIs: "Will registration is NOT mandatory in India (registered or unregistered wills are valid). However, registration avoids disputes and speeds up probate. Cost: ₹6-8K approx. Highly recommended.",
      faqs: [
        {
          question: "Is Will registration mandatory?",
          answer: "No. Indian law doesn't require registration. However, registered Will: avoids disputes, speeds probate process, serves as strong evidence. Unregistered may face challenges by aggrieved heirs.",
        },
        {
          question: "Can I write Will myself without lawyer?",
          answer: "Yes, legally valid if properly drafted and witnessed (by 2 people). But errors can invalidate it or cause disputes. Lawyer-drafted Will (~₹3-5K) prevents costly litigation later.",
        },
        {
          question: "What happens if I die without a Will?",
          answer: "Property goes under Succession Act rules (varies by religion). Intestate succession: property divided among legal heirs per government rules (not your choice). Messy, lengthy, expensive. Write a Will.",
        },
        {
          question: "Can I change my Will?",
          answer: "Yes, anytime. Write new Will, destroy old one. Each new Will cancels previous. Can also add Codicil (amendment). Registration doesn't limit changes.",
        },
      ],
      relatedSlugs: ["legal/power-of-attorney-cost", "legal/notarization-fee-calculator"],
    },
  },

  // 8. Power of Attorney Cost
  {
    slug: "power-of-attorney-cost",
    category: "legal",
    title: "Power of Attorney Registration Cost",
    shortTitle: "Power of Attorney",
    description: "Calculate power of attorney registration cost",
    keywords: ["poa", "power", "attorney", "registration", "cost", "stamp"],
    inputs: [
      { type: "select", key: "poaType", label: "Type of POA", options: [
        { value: "general", label: "General POA (All Affairs)" },
        { value: "limited", label: "Limited POA (Specific Purpose)" },
        { value: "irrevocable", label: "Irrevocable POA" }
      ], default: "general" },
      { type: "toggle", key: "withLawyer", label: "Use Lawyer to Prepare?", default: true },
    ],
    calculate(v) {
      const type = v.poaType as string;
      const withLawyer = v.withLawyer as boolean;

      // Stamp duty (varies by state and POA type)
      const stampDutyRates: Record<string, number> = {
        general: 100,
        limited: 50,
        irrevocable: 200,
      };
      const stampDuty = stampDutyRates[type] ?? 100;

      // Registration fee
      const registrationFee = 1000;

      // Notarization fee
      const notaryFee = 500;

      // Lawyer fee
      const lawyerFee = withLawyer ? 3000 : 0;

      const totalCost = stampDuty + registrationFee + notaryFee + lawyerFee;

      return {
        poaType: type,
        stampDuty: Math.round(stampDuty),
        registrationFee: Math.round(registrationFee),
        notaryFee: Math.round(notaryFee),
        lawyerFee: Math.round(lawyerFee),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total POA Registration Cost", format: "currency", isPrimary: true },
      { key: "stampDuty", label: "Stamp Duty", format: "currency" },
      { key: "registrationFee", label: "Registration Fee", format: "currency" },
      { key: "lawyerFee", label: "Lawyer Fee", format: "currency" },
    ],
    content: {
      whatIs: "Power of Attorney authorizes someone to act on your behalf. Registration (optional but recommended) makes it legally stronger. Types: General (all matters), Limited (specific), Irrevocable (can't revoke). Cost: ₹4-6K approx.",
      faqs: [
        {
          question: "Should I register my POA?",
          answer: "Highly recommended, especially for property/financial matters. Registration creates official record, prevents disputes. Unregistered POA can be challenged later. ₹5K investment worth it.",
        },
        {
          question: "Can I revoke POA?",
          answer: "Yes, unless irrevocable. Revocation needs written notice, registered if original was registered. Irrevocable POA can't be revoked (given for loans, etc.). Think before making irrevocable.",
        },
        {
          question: "What if agent misuses POA?",
          answer: "You can file civil suit for damages or criminal complaint. Registration creates proof of authorization limits. Unregistered POA gives agent more wiggle room to claim exceeded authority.",
        },
        {
          question: "Do I need POA for everything?",
          answer: "No. Only for matters you authorize. POA can be limited (sale of specific property) or general (all affairs). Broader POA = more risk, but more convenience. Balance per need.",
        },
      ],
      relatedSlugs: ["legal/will-registration-cost", "legal/partnership-deed-cost"],
    },
  },

  // 9-20: Remaining legal tools
  {
    slug: "partnership-deed-cost",
    category: "legal",
    title: "Partnership Deed Registration Cost",
    shortTitle: "Partnership Deed",
    description: "Calculate partnership deed registration cost",
    keywords: ["partnership", "deed", "registration", "cost", "stamp", "legal"],
    inputs: [
      { type: "number", key: "partnerCapital", label: "Total Partnership Capital", prefix: "₹", min: 0, max: 50000000, step: 100000, default: 1000000 },
      { type: "toggle", key: "withLawyer", label: "Use Lawyer?", default: true },
    ],
    calculate(v) {
      const capital = v.partnerCapital as number;
      const withLawyer = v.withLawyer as boolean;

      const stampDuty = Math.max(100, (capital * 0.01) / 100); // 0.01% approx
      const registrationFee = 500;
      const notaryFee = 500;
      const lawyerFee = withLawyer ? 5000 : 0;

      const totalCost = stampDuty + registrationFee + notaryFee + lawyerFee;

      return {
        partnerCapital: Math.round(capital),
        stampDuty: Math.round(stampDuty),
        registrationFee: Math.round(registrationFee),
        notaryFee: Math.round(notaryFee),
        lawyerFee: Math.round(lawyerFee),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total Partnership Deed Cost", format: "currency", isPrimary: true },
      { key: "stampDuty", label: "Stamp Duty", format: "currency" },
      { key: "lawyerFee", label: "Lawyer Fee", format: "currency" },
    ],
    content: {
      whatIs: "Partnership deed outlines terms: profit sharing, capital contribution, roles, dispute resolution. Not legally required but highly recommended to prevent disputes. Cost: ₹5-8K.",
      faqs: [
        {
          question: "Is partnership deed mandatory?",
          answer: "No, but essential. Without deed, profit splits equally and any partner can dissolve firm. With deed, you control structure. Prevents costly disputes later.",
        },
      ],
      relatedSlugs: ["business/partnership-profit-sharing", "business/company-registration-cost"],
    },
  },

  {
    slug: "company-incorporation-cost",
    category: "legal",
    title: "Company Incorporation Cost (MOA & AOA)",
    shortTitle: "Company Incorporation",
    description: "Calculate company incorporation cost India",
    keywords: ["company", "incorporation", "cost", "moa", "aoa", "registration"],
    inputs: [
      { type: "number", key: "authorizedCapital", label: "Authorized Capital", prefix: "₹", min: 100000, max: 50000000, step: 100000, default: 1000000 },
    ],
    calculate(v) {
      const capital = v.authorizedCapital as number;

      const mcaFee = 2500;
      const stampDuty = Math.max(500, (capital * 0.0001));
      const dscCost = 2000;
      const legalFee = 5000;

      const totalCost = mcaFee + stampDuty + dscCost + legalFee;

      return {
        authorizedCapital: Math.round(capital),
        mcaFee: Math.round(mcaFee),
        stampDuty: Math.round(stampDuty),
        dscCost: Math.round(dscCost),
        legalFee: Math.round(legalFee),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total Incorporation Cost", format: "currency", isPrimary: true },
      { key: "mcaFee", label: "MCA Filing Fee", format: "currency" },
      { key: "stampDuty", label: "Stamp Duty", format: "currency" },
      { key: "dscCost", label: "Digital Signature Cost", format: "currency" },
    ],
    content: {
      whatIs: "Incorporating a company requires filing MOA (Memorandum) and AOA (Articles) with MCA. Cost includes government fees, stamp duty, DSC, legal. Total: ₹10-15K.",
      faqs: [
        {
          question: "What's MOA vs AOA?",
          answer: "MOA: Constitution of company (objects, capital). AOA: Internal governance (meetings, roles, voting). Both required. MOA governs external dealings; AOA internal management.",
        },
      ],
      relatedSlugs: ["business/company-registration-cost", "legal/trademark-filing-fee"],
    },
  },

  {
    slug: "trademark-filing-fee",
    category: "legal",
    title: "Trademark Filing Fee Calculator",
    shortTitle: "Trademark Filing",
    description: "Calculate trademark filing fee in India",
    keywords: ["trademark", "filing", "fee", "government", "ip", "office"],
    inputs: [
      { type: "number", key: "classCount", label: "Number of Classes", suffix: "classes", min: 1, max: 45, step: 1, default: 1 },
    ],
    calculate(v) {
      const classes = v.classCount as number;
      const govFeePerClass = 4500;
      const totalGovFee = govFeePerClass * classes;

      return {
        classCount: classes,
        govFeePerClass: govFeePerClass,
        totalGovFee: Math.round(totalGovFee),
      };
    },
    resultFields: [
      { key: "totalGovFee", label: "Government Filing Fee", format: "currency", isPrimary: true },
      { key: "govFeePerClass", label: "Fee Per Class", format: "currency" },
      { key: "classCount", label: "Number of Classes", format: "number" },
    ],
    content: {
      whatIs: "Trademark government filing fee: ₹4,500 per class. You need 1-10 classes depending on business type. Total government fee ₹4.5-45K. Add agent/lawyer fees (~₹5-10K) for total cost ₹10-55K.",
      faqs: [
        {
          question: "Why multiple classes?",
          answer: "45 classes categorize goods/services. Class 1-34: goods. 35-45: services. If selling multiple products/services, file in all relevant classes. Each class = separate protection.",
        },
      ],
      relatedSlugs: ["business/trademark-registration-cost", "legal/company-incorporation-cost"],
    },
  },

  {
    slug: "fssai-license-cost",
    category: "legal",
    title: "FSSAI Food Business License Cost",
    shortTitle: "FSSAI License",
    description: "Calculate FSSAI license registration cost",
    keywords: ["fssai", "food", "license", "registration", "cost"],
    inputs: [
      { type: "select", key: "businessType", label: "Food Business Type", options: [
        { value: "manufacture", label: "Food Manufacture" },
        { value: "processing", label: "Food Processing" },
        { value: "retail", label: "Retail (Shop)" },
        { value: "restaurant", label: "Restaurant/Cafe" },
        { value: "catering", label: "Catering Service" }
      ], default: "retail" },
      { type: "number", key: "turnover", label: "Expected Annual Turnover", prefix: "₹", min: 0, max: 10000000, step: 100000, default: 500000 },
    ],
    calculate(v) {
      const type = v.businessType as string;
      const turnover = v.turnover as number;

      // FSSAI registration fees vary by type and turnover
      const registrationFee = turnover > 5000000 ? 2800 : turnover > 1000000 ? 1800 : 1000;
      const applicableFee = 0; // Online, no payment needed if < turnover threshold

      const totalGovFee = registrationFee;

      // Consultant/lawyer fee
      const consultantFee = 3000;

      // Compliance audit (optional but recommended)
      const auditFee = 2000;

      const totalCost = totalGovFee + consultantFee + auditFee;

      return {
        businessType: type,
        governmentFee: Math.round(totalGovFee),
        consultantFee: Math.round(consultantFee),
        auditFee: Math.round(auditFee),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total FSSAI License Cost", format: "currency", isPrimary: true },
      { key: "governmentFee", label: "Government Registration Fee", format: "currency" },
      { key: "consultantFee", label: "Consultant/Helper Fee", format: "currency" },
      { key: "auditFee", label: "Compliance Audit", format: "currency" },
    ],
    content: {
      whatIs: "FSSAI (Food Safety & Standards Authority of India) license is mandatory for food businesses. Online registration, fees ₹1,000-2,800 depending on turnover. Processing takes ~3 weeks.",
      faqs: [
        {
          question: "Who needs FSSAI license?",
          answer: "Any food business: manufacture, processing, import, distribution, retail, catering. Except: home-made food (license not needed), unlicensed still legal but risky.",
        },
      ],
      relatedSlugs: ["business/company-registration-cost", "business/msme-registration-benefits"],
    },
  },

  {
    slug: "iec-registration-cost",
    category: "legal",
    title: "IEC Registration Cost (Import-Export Code)",
    shortTitle: "IEC Cost",
    description: "Calculate IEC registration cost for export",
    keywords: ["iec", "import", "export", "code", "registration", "trade"],
    inputs: [
      { type: "number", key: "annualTurnover", label: "Expected Annual Turnover", prefix: "₹", min: 0, max: 100000000, step: 1000000, default: 5000000 },
    ],
    calculate(v) {
      const turnover = v.annualTurnover as number;

      // Government fee (nominal, online)
      const govFee = 0; // Now free online

      // Consultant fee for application
      const consultantFee = 2500;

      // Documentation support
      const docFee = 1500;

      const totalCost = govFee + consultantFee + docFee;

      return {
        annualTurnover: Math.round(turnover),
        governmentFee: 0,
        consultantFee: Math.round(consultantFee),
        documentationFee: Math.round(docFee),
        totalCost: Math.round(totalCost),
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total IEC Registration Cost", format: "currency", isPrimary: true },
      { key: "consultantFee", label: "Consultant/Agent Fee", format: "currency" },
      { key: "documentationFee", label: "Documentation Support Fee", format: "currency" },
    ],
    content: {
      whatIs: "IEC (Import-Export Code) is mandatory for importing/exporting goods. Government fee: free (online). Consultant fee: ₹2-3K. Processing: 24 hours online.",
      faqs: [
        {
          question: "Do I need IEC for selling online internationally?",
          answer: "Only if exporting physical goods. For digital services, not needed. For goods (handmade, art, etc.) exported via e-commerce, IEC recommended (enables GST benefit).",
        },
      ],
      relatedSlugs: ["business/company-registration-cost", "business/gst-calculator"],
    },
  },

  {
    slug: "police-verification-fee",
    category: "legal",
    title: "Police Verification Fee Calculator",
    shortTitle: "Police Verification",
    description: "Calculate police verification fee charges",
    keywords: ["police", "verification", "fee", "passport", "visa", "clearance"],
    inputs: [
      { type: "select", key: "purpose", label: "Purpose of Verification", options: [
        { value: "passport", label: "Passport Application" },
        { value: "visa", label: "Visa/Immigration" },
        { value: "employment", label: "Employment Verification" },
        { value: "loan", label: "Loan/Bank Verification" },
        { value: "other", label: "Other" }
      ], default: "passport" },
    ],
    calculate(v) {
      const purpose = v.purpose as string;

      // Police verification fees (typically free or nominal in India)
      const fees: Record<string, number> = {
        passport: 0, // Free (part of government service)
        visa: 0,
        employment: 500,
        loan: 500,
        other: 500,
      };

      const fee = fees[purpose] ?? 0;

      return {
        purpose: purpose,
        policeFee: fee,
        processingDays: 3,
      };
    },
    resultFields: [
      { key: "policeFee", label: "Police Verification Fee", format: "currency", isPrimary: true },
    ],
    content: {
      whatIs: "Police verification is used for passport, visa, employment background checks. Usually free for passport (part of government process). Processing: 3-5 days. May require visit to local police station.",
      faqs: [
        {
          question: "Is police verification mandatory for passport?",
          answer: "Yes, for first-time passports. Already verified if renewing same category. Verification is free; you submit form at local police station. Processing: 3-5 days.",
        },
      ],
      relatedSlugs: ["legal/passport-fee-calculator", "legal/pan-card-fee"],
    },
  },

  {
    slug: "passport-fee-calculator",
    category: "legal",
    title: "Passport Application Fee Calculator",
    shortTitle: "Passport Fee",
    description: "Calculate passport fee for application",
    keywords: ["passport", "fee", "application", "validity", "years"],
    inputs: [
      { type: "select", key: "passportType", label: "Passport Type", options: [
        { value: "ordinary", label: "Ordinary (Regular)" },
        { value: "tatkal", label: "Tatkal (Urgent)" }
      ], default: "ordinary" },
      { type: "select", key: "validity", label: "Passport Validity", options: [
        { value: "10year", label: "10 Years (Adult)" },
        { value: "5year", label: "5 Years (Minor/Limited)" }
      ], default: "10year" },
    ],
    calculate(v) {
      const type = v.passportType as string;
      const validity = v.validity as string;

      // Government fees
      let applicationFee = 0;
      if (validity === "10year") applicationFee = 1500;
      else applicationFee = 1000;

      const tatkalSurcharge = type === "tatkal" ? 2000 : 0;

      const totalFee = applicationFee + tatkalSurcharge;

      return {
        passportType: type,
        validity: validity,
        applicationFee: applicationFee,
        tatkalSurcharge: tatkalSurcharge,
        totalFee: totalFee,
      };
    },
    resultFields: [
      { key: "totalFee", label: "Total Passport Fee", format: "currency", isPrimary: true },
      { key: "applicationFee", label: "Application Fee", format: "currency" },
      { key: "tatkalSurcharge", label: "Tatkal Surcharge (If Urgent)", format: "currency" },
    ],
    content: {
      whatIs: "Passport application fees: ₹1,500 (10-year ordinary), ₹1,000 (5-year ordinary), + ₹2,000 (Tatkal urgent). Filing online, processing 10-15 days ordinary, 1-3 days Tatkal.",
      faqs: [
        {
          question: "What's Tatkal passport?",
          answer: "Urgent passport service. Surcharge ₹2,000. Processing 1-3 days. Use when traveling urgently. Requires valid reason, proof of travel.",
        },
      ],
      relatedSlugs: ["legal/police-verification-fee", "legal/driving-license-fee"],
    },
  },

  {
    slug: "driving-license-fee",
    category: "legal",
    title: "Driving License Fee Calculator by State",
    shortTitle: "Driving License Fee",
    description: "Calculate driving license fee by state",
    keywords: ["driving", "license", "fee", "state", "application", "renewal"],
    inputs: [
      { type: "select", key: "state", label: "State", options: [
        { value: "maharashtra", label: "Maharashtra" },
        { value: "karnataka", label: "Karnataka" },
        { value: "tamil_nadu", label: "Tamil Nadu" },
        { value: "delhi", label: "Delhi" },
        { value: "punjab", label: "Punjab" },
      ], default: "maharashtra" },
      { type: "select", key: "type", label: "Application Type", options: [
        { value: "new", label: "New Driving License" },
        { value: "renewal", label: "Renewal" }
      ], default: "new" },
    ],
    calculate(v) {
      const state = v.state as string;
      const type = v.type as string;

      // Fees vary by state
      const fees: Record<string, Record<string, number>> = {
        maharashtra: { new: 800, renewal: 300 },
        karnataka: { new: 700, renewal: 300 },
        tamil_nadu: { new: 1000, renewal: 500 },
        delhi: { new: 900, renewal: 300 },
        punjab: { new: 600, renewal: 200 },
      };

      const fee = fees[state]?.[type] ?? 800;

      return {
        state: state,
        type: type,
        governmentFee: fee,
      };
    },
    resultFields: [
      { key: "governmentFee", label: "Driving License Fee", format: "currency", isPrimary: true },
    ],
    content: {
      whatIs: "Driving license fees: ₹600-1,000 (new), ₹200-500 (renewal) by state. Application online, processing 3-7 days. Renewal valid for 5-10 years.",
      faqs: [
        {
          question: "How long is DL valid?",
          answer: "Usually 5-10 years from issue. Depends on age at issue (16-18 year old: 3 years initially). Can renew before expiry. Renewal process simpler than new.",
        },
      ],
      relatedSlugs: ["legal/passport-fee-calculator", "legal/pan-card-fee"],
    },
  },

  {
    slug: "pan-card-fee",
    category: "legal",
    title: "PAN Card Application Fee",
    shortTitle: "PAN Card Fee",
    description: "Calculate PAN card application fee in India",
    keywords: ["pan", "card", "fee", "application", "income", "tax"],
    inputs: [
      { type: "radio", key: "applicationType", label: "Application Type", options: [
        { value: "new", label: "New PAN" },
        { value: "change", label: "Change/Correction" }
      ], default: "new" },
    ],
    calculate(v) {
      const type = v.applicationType as string;

      // Government fee (NSDL)
      const newPanFee = 107; // Including ST
      const changeFee = 107;

      const fee = type === "new" ? newPanFee : changeFee;

      // Processing time varies
      const processingDays = type === "new" ? 3 : 3;

      return {
        applicationType: type,
        panFee: fee,
        processingDays: processingDays,
      };
    },
    resultFields: [
      { key: "panFee", label: "PAN Card Application Fee", format: "currency", isPrimary: true },
    ],
    content: {
      whatIs: "PAN card is mandatory for income tax filing, bank accounts, investments. Application fee: ₹107 (minimal). Online application, processing 3-7 days. PAN is lifetime.",
      faqs: [
        {
          question: "Is PAN mandatory?",
          answer: "Yes, if income > ₹2.5L p.a. Also needed for: bank accounts, GST registration, investments, loan applications, etc. Get it early.",
        },
      ],
      relatedSlugs: ["legal/gst-registration-cost", "legal/passport-fee-calculator"],
    },
  },

  {
    slug: "gst-registration-cost",
    category: "legal",
    title: "GST Registration Cost",
    shortTitle: "GST Registration",
    description: "Calculate GST registration cost fees",
    keywords: ["gst", "registration", "cost", "business", "tax"],
    inputs: [
      { type: "number", key: "expectedTurnover", label: "Expected Annual Turnover", prefix: "₹", min: 100000, max: 50000000, step: 500000, default: 2000000 },
    ],
    calculate(v) {
      const turnover = v.expectedTurnover as number;

      // Government fee (free online via Udyam/GST portal)
      const govFee = 0;

      // Consultant/CA fee for filing
      const caFee = turnover > 5000000 ? 3000 : 2000;

      const totalCost = govFee + caFee;

      return {
        expectedTurnover: Math.round(turnover),
        governmentFee: 0,
        consultantFee: caFee,
        totalCost: totalCost,
      };
    },
    resultFields: [
      { key: "totalCost", label: "Total GST Registration Cost", format: "currency", isPrimary: true },
      { key: "governmentFee", label: "Government Fee", format: "currency" },
      { key: "consultantFee", label: "CA/Consultant Fee", format: "currency" },
    ],
    content: {
      whatIs: "GST registration is free online. Takes 1-3 days. Consultant fee (₹2-3K) if using CA to file. Mandatory if turnover > ₹40L (goods) or ₹20L (services).",
      faqs: [
        {
          question: "Do I need GST if turnover is below limit?",
          answer: "Not mandatory, but recommended. Benefits: ITC on inputs, professional image, eligibility for government tenders. Voluntary registration even below threshold is allowed.",
        },
      ],
      relatedSlugs: ["business/gst-calculator", "business/company-registration-cost"],
    },
  },

  {
    slug: "digital-signature-cost",
    category: "legal",
    title: "Digital Signature Certificate (DSC) Cost",
    shortTitle: "DSC Cost",
    description: "Calculate digital signature certificate cost",
    keywords: ["digital", "signature", "dsc", "certificate", "cost", "class"],
    inputs: [
      { type: "select", key: "dscClass", label: "DSC Class", options: [
        { value: "class2", label: "Class 2 (General Purpose)" },
        { value: "class3", label: "Class 3 (High Security)" }
      ], default: "class2" },
      { type: "select", key: "validity", label: "Validity Period", options: [
        { value: "1year", label: "1 Year" },
        { value: "2year", label: "2 Years" }
      ], default: "1year" },
    ],
    calculate(v) {
      const dscClass = v.dscClass as string;
      const validity = v.validity as string;

      // DSC fees by class and validity
      const fees: Record<string, Record<string, number>> = {
        class2: { "1year": 1000, "2year": 1500 },
        class3: { "1year": 2000, "2year": 3000 },
      };

      const fee = fees[dscClass]?.[validity] ?? 1000;

      return {
        dscClass: dscClass,
        validity: validity,
        dscFee: fee,
      };
    },
    resultFields: [
      { key: "dscFee", label: "DSC Certificate Cost", format: "currency", isPrimary: true },
    ],
    content: {
      whatIs: "Digital Signature Certificate (DSC) is required for e-filing, online transactions. Class 2: general (₹1-1.5K). Class 3: high security (₹2-3K). Valid 1-2 years. Issued by authorized CAs.",
      faqs: [
        {
          question: "Do I need DSC?",
          answer: "Required for: ITR e-filing, company filing, GST return, legal documents. Highly recommended for any business. Cost minimal (₹1K+), validity long (1-2 years).",
        },
      ],
      relatedSlugs: ["business/company-registration-cost", "legal/gst-registration-cost"],
    },
  },

  {
    slug: "agreement-drafting-cost",
    category: "legal",
    title: "Legal Agreement Drafting Cost Estimator",
    shortTitle: "Agreement Drafting",
    description: "Calculate agreement drafting cost fees",
    keywords: ["agreement", "drafting", "cost", "legal", "contract"],
    inputs: [
      { type: "select", key: "agreementType", label: "Agreement Type", options: [
        { value: "sale", label: "Purchase/Sale Agreement" },
        { value: "rent", label: "Rent Agreement" },
        { value: "partnership", label: "Partnership Deed" },
        { value: "loan", label: "Loan Agreement" },
        { value: "service", label: "Service Agreement" },
        { value: "nda", label: "NDA / Confidentiality" },
        { value: "employment", label: "Employment Contract" }
      ], default: "rent" },
      { type: "select", key: "complexity", label: "Agreement Complexity", options: [
        { value: "simple", label: "Simple & Straightforward" },
        { value: "moderate", label: "Moderate Complexity" },
        { value: "complex", label: "Complex with Multiple Terms" }
      ], default: "simple" },
    ],
    calculate(v) {
      const type = v.agreementType as string;
      const complexity = v.complexity as string;

      // Base drafting fees by type
      const baseFees: Record<string, number> = {
        sale: 3000,
        rent: 2000,
        partnership: 2500,
        loan: 3000,
        service: 2500,
        nda: 1500,
        employment: 2500,
      };

      const base = baseFees[type] ?? 2500;

      // Complexity multiplier
      const multipliers: Record<string, number> = {
        simple: 1,
        moderate: 1.5,
        complex: 2.5,
      };

      const multiplier = multipliers[complexity] ?? 1;

      const totalFee = base * multiplier;

      return {
        agreementType: type,
        complexity: complexity,
        baseFee: Math.round(base),
        totalFee: Math.round(totalFee),
      };
    },
    resultFields: [
      { key: "totalFee", label: "Agreement Drafting Cost", format: "currency", isPrimary: true },
      { key: "baseFee", label: "Base Fee (Simple)", format: "currency" },
      { key: "agreementType", label: "Agreement Type", format: "text" },
    ],
    content: {
      whatIs: "Legal agreement drafting cost varies by type and complexity. Simple rent agreement: ₹1.5-2K. Complex commercial agreement: ₹5-10K+. DIY drafting is risky; lawyer drafting avoids costly disputes.",
      faqs: [
        {
          question: "Should I use lawyer or template?",
          answer: "Templates (₹100-500) are cheap but risky. Lawyer (₹1.5-5K) customizes to your needs, avoids gaps. For high-value agreements (property, loans, partnership), lawyer is worth it.",
        },
      ],
      relatedSlugs: ["legal/legal-notice-cost", "legal/advocate-fee-estimator"],
    },
  },
];
